import axios from 'axios';
import JSZip from 'jszip';
import Swal from 'sweetalert2';
import { fetchBlobUrl } from '../components/subtitles/ReadBlobToString';
import { getSubNodesfromVtt, sub2srt } from '../libs/readSub/SubUtils';
import videoCRUDServiceInstance from './VideoCRUDService';

class VideoDownloadService {
    /**
     * Download all video-related files
     * @param {string} videoId - The video ID
     * @param {Object} videoData - The video data object
     */
    async downloadVideoFiles(videoId, videoData = null) {
        try {
            // Get video data if not provided
            if (!videoData) {
                const response = await videoCRUDServiceInstance.getVideoById(videoId);
                videoData = response.data;
            }

            const downloadList = await this.generateDownloadList(videoData);

            // Show download confirmation dialog
            const shouldDownload = await this.showDownloadDialog(downloadList);
            if (!shouldDownload) return;

            // Small delay to ensure proper cleanup of previous dialog state
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Update the dialog to show progress instead of creating a new one
            Swal.update({
                title: 'Preparing Download',
                text: 'Collecting and preparing files...',
                icon: undefined,
                showCancelButton: false,
                showConfirmButton: false,
                allowOutsideClick: false
            });
            Swal.showLoading();

            // Start downloads
            await this.executeDownloads(downloadList, videoData);
        } catch (error) {
            console.error('Error downloading video files:', error);
            Swal.fire({
                title: 'Download Failed',
                text: 'Failed to download video files: ' + error.message,
                icon: 'error'
            });
        }
    }

    /**
     * Generate list of files to download
     */
    async generateDownloadList(videoData) {
        return this.generateDownloadListWithOptions(videoData, {});
    }

    async generateDownloadListWithOptions(videoData, options = {}) {
        const files = [];
        const videoName = videoData.videoName || 'video';
        const currentTime = new Date().toISOString().replace(/[:.]/g, '-');
        const includeOriginalVideo = options.includeOriginalVideo !== false;
        const includeCompressedVideo = options.includeCompressedVideo !== false;
        const includeSrtFiles = options.includeSrtFiles !== false;
        const selectedLanguages = new Set(
            (Array.isArray(options.selectedLanguages) ? options.selectedLanguages : [])
                .map((name) => String(name || '').trim().toLowerCase())
                .filter(Boolean),
        );

        // 1. Original video source info
        if (includeOriginalVideo && videoData.videoUrl) {
            const linkType = this.detectLinkType(videoData.videoUrl);

            if (linkType === 'gdrive' || linkType === 'youtube') {
                files.push({
                    name: `${videoName}_source_link_${currentTime}.txt`,
                    type: 'text',
                    content: videoData.videoUrl,
                    description: `${linkType === 'gdrive' ? 'Google Drive' : 'YouTube'} source link`,
                });
            } else {
                // Local file - download original
                files.push({
                    name: `${videoName}_original.${this.getFileExtension(videoData.videoUrl)}`,
                    type: 'video',
                    url: videoData.videoUrl,
                    description: 'Original video file',
                });
            }
        }

        // 2. Compressed video (always download if available)
        if (includeCompressedVideo && videoData.compressedUrl) {
            files.push({
                name: `${videoName}_compressed.${this.getFileExtension(videoData.compressedUrl)}`,
                type: 'video',
                url: videoData.compressedUrl,
                description: 'Compressed video file',
            });
        }

        // 3. VTT files for each language (converted to SRT)
        if (includeSrtFiles && videoData.videoLanguages && Array.isArray(videoData.videoLanguages)) {
            for (const lang of videoData.videoLanguages) {
                const languageName = String(lang.languageName || '').trim();
                const normalizedName = languageName.toLowerCase();
                const languageAllowed = selectedLanguages.size === 0 || selectedLanguages.has(normalizedName);

                if (lang.vttFileUrl && lang.status === 'READY' && languageAllowed) {
                    files.push({
                        name: `${videoName}_${languageName}.srt`,
                        type: 'vtt-to-srt',
                        url: lang.vttFileUrl,
                        description: `${languageName} subtitles (converted to SRT)`,
                    });
                }
            }
        }

        return files;
    }

    /**
     * Show download confirmation dialog
     */
    async showDownloadDialog(downloadList) {
        return new Promise((resolve) => {
            const fileListHtml = downloadList
                .map(
                    (file) =>
                        `<div style="margin: 5px 0; padding: 5px; border-left: 3px solid #007bff;">
                    <strong>${file.name}</strong><br>
                    <small style="color: #666;">${file.description}</small>
                </div>`,
                )
                .join('');

            const dialogHtml = `
                <div style="text-align: left;">
                    <p>The following files will be downloaded:</p>
                    ${fileListHtml}
                    <p style="margin-top: 15px;"><strong>Total files: ${downloadList.length}</strong></p>
                </div>
            `;

            Swal.fire({
                title: 'Download Video Files',
                html: dialogHtml,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Download All',
                cancelButtonText: 'Cancel',
                width: '600px',
                backdrop: true,
                allowOutsideClick: false,
                customClass: {
                    popup: 'download-confirmation-dialog'
                },
                didOpen: () => {
                    // Remove focus from any previously focused elements to prevent aria-hidden conflicts
                    if (document.activeElement && document.activeElement !== document.body) {
                        document.activeElement.blur();
                    }
                    
                    // Ensure dialog is on top
                    const popup = Swal.getPopup();
                    if (popup) {
                        popup.style.zIndex = '999999';
                    }
                    // Also set backdrop z-index
                    const backdrop = document.querySelector('.swal2-backdrop');
                    if (backdrop) {
                        backdrop.style.zIndex = '999998';
                    }
                },
                willClose: () => {
                    // Ensure any remaining focus is cleared before closing
                    if (document.activeElement && document.activeElement !== document.body) {
                        document.activeElement.blur();
                    }
                }
            }).then((result) => {
                resolve(result.isConfirmed);
            });
        });
    }

    /**
     * Execute downloads - creates a ZIP file with all content
     */
    async executeDownloads(downloadList, videoData) {
        const zip = new JSZip();
        const videoName = videoData.videoName || 'video';
        let successCount = 0;
        let errorCount = 0;

        // Dialog is already open from downloadVideoFiles, just update it
        for (const file of downloadList) {
            try {
                const fileContent = await this.getFileContent(file);

                if (file.type === 'text') {
                    zip.file(file.name, fileContent);
                } else if (file.type === 'vtt-to-srt') {
                    zip.file(file.name, fileContent);
                } else {
                    zip.file(file.name, fileContent);
                }

                successCount++;

                // Update progress
                Swal.update({
                    text: `Processed ${successCount}/${downloadList.length} files...`,
                });
            } catch (error) {
                console.error(`Failed to process ${file.name}:`, error);
                errorCount++;
            }
        }

        try {
            // Generate ZIP file
            Swal.update({
                text: 'Creating ZIP file...',
            });

            const zipContent = await zip.generateAsync({ type: 'blob' });
            const currentTime = new Date().toISOString().replace(/[:.]/g, '-');
            const zipFileName = `${videoName}_files_${currentTime}.zip`;

            // Download the ZIP file
            this.downloadBlob(zipContent, zipFileName);

            Swal.close();

            // Show completion message
            Swal.fire({
                title: 'Download Complete',
                text: `Successfully created ZIP file with ${successCount} files. ${errorCount} failed.`,
                icon: successCount > 0 ? 'success' : 'error',
                didOpen: () => {
                    // Ensure completion dialog is on top
                    const popup = Swal.getPopup();
                    if (popup) {
                        popup.style.zIndex = '1000000';
                    }
                    const backdrop = document.querySelector('.swal2-backdrop');
                    if (backdrop) {
                        backdrop.style.zIndex = '999999';
                    }
                }
            });
        } catch (error) {
            Swal.close();
            throw new Error(`Failed to create ZIP file: ${error.message}`);
        }
    }

    /**
     * Get file content for ZIP creation
     */
    async getFileContent(file) {
        if (file.type === 'text') {
            return file.content;
        } else if (file.type === 'vtt-to-srt') {
            // Download VTT file from blob storage and convert to SRT
            const vttContent = await fetchBlobUrl(file.url);
            if (vttContent) {
                try {
                    // Convert VTT content to structured subtitle data
                    const subtitleData = getSubNodesfromVtt(vttContent);
                    // Convert to SRT format
                    const srtContent = sub2srt(subtitleData, false);
                    return srtContent;
                } catch (conversionError) {
                    console.error('VTT to SRT conversion failed:', conversionError);
                    throw new Error(`Failed to convert VTT to SRT: ${conversionError.message}`);
                }
            } else {
                throw new Error('Failed to fetch VTT content');
            }
        } else {
            // Download video file
            const response = await axios.get(file.url, {
                responseType: 'blob',
                timeout: 300000, // 5 minute timeout for large files
            });
            return response.data;
        }
    }

    /**
     * Download individual file (kept for backward compatibility)
     */
    async downloadFile(file) {
        const content = await this.getFileContent(file);

        if (file.type === 'text' || file.type === 'vtt-to-srt') {
            const blob = new Blob([content], { type: 'text/plain' });
            this.downloadBlob(blob, file.name);
        } else {
            this.downloadBlob(content, file.name);
        }
    }

    /**
     * Download blob as file
     */
    downloadBlob(blob, filename) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();

        setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }, 100);
    }

    /**
     * Detect link type from URL
     */
    detectLinkType(url) {
        if (url.includes('youtube.com') || url.includes('youtu.be')) {
            return 'youtube';
        } else if (url.includes('drive.google.com')) {
            return 'gdrive';
        }
        return 'local';
    }

    /**
     * Get file extension from URL
     */
    getFileExtension(url) {
        const match = url.match(/\.([^./?]+)(?:[?]|$)/);
        return match ? match[1] : 'mp4';
    }

    /**
     * Download multiple videos from a folder
     * @param {Array} videoFiles - Array of video file objects from the folder
     * @param {string} folderName - Name of the folder being downloaded
     */
    async downloadFolderVideos(videoFiles, folderName = 'folder', options = {}) {
        if (!videoFiles || videoFiles.length === 0) {
            throw new Error('No video files to download');
        }

        try {
            const allDownloadLists = [];
            const allVideoData = [];

            // Show initial progress
            Swal.fire({
                title: 'Preparing Folder Download',
                text: 'Analyzing folder contents...',
                allowOutsideClick: false,
                backdrop: true,
                didOpen: () => {
                    Swal.showLoading();
                    // Ensure dialog is on top
                    const popup = Swal.getPopup();
                    if (popup) {
                        popup.style.zIndex = '100000';
                    }
                },
            });

            // Generate download lists for each video
            for (let i = 0; i < videoFiles.length; i++) {
                const videoFile = videoFiles[i];
                try {
                    Swal.update({
                        text: `Analyzing video ${i + 1}/${videoFiles.length}: ${videoFile.videoName || videoFile.name}...`,
                    });

                    const videoId = videoFile.data || videoFile._id || videoFile.id;
                    if (!videoId) {
                        console.warn(`Skipping video without ID: ${videoFile.videoName}`);
                        continue;
                    }

                    const response = await videoCRUDServiceInstance.getVideoById(videoId);
                    const videoData = response.data;
                    const downloadList = await this.generateDownloadListWithOptions(videoData, options);
                    if (downloadList.length === 0) {
                        continue;
                    }
                    
                    allDownloadLists.push({
                        videoName: videoData.videoName || `video_${i + 1}`,
                        downloadList,
                        videoData
                    });
                    allVideoData.push(videoData);
                } catch (error) {
                    console.error(`Failed to analyze video ${videoFile.videoName}:`, error);
                }
            }

            if (allDownloadLists.length === 0) {
                throw new Error('No valid videos found to download');
            }

            // Show confirmation dialog with all files
            const shouldDownload = await this.showFolderDownloadDialog(allDownloadLists, folderName);
            if (!shouldDownload) return;

            // Execute bulk downloads
            await this.executeFolderDownloads(allDownloadLists, folderName);

        } catch (error) {
            Swal.close();
            console.error('Error downloading folder videos:', error);
            Swal.fire({
                icon: 'error',
                title: 'Download Failed',
                text: error.message || 'Failed to download folder contents',
            });
        }
    }

    /**
     * Show folder download confirmation dialog
     */
    async showFolderDownloadDialog(allDownloadLists, folderName) {
        return new Promise((resolve) => {
            const totalFiles = allDownloadLists.reduce((sum, item) => sum + item.downloadList.length, 0);
            
            const videoListHtml = allDownloadLists
                .map((item) => {
                    const fileList = item.downloadList
                        .map(file => `<li style="margin: 2px 0; font-size: 12px;">${file.name}</li>`)
                        .join('');
                    
                    return `
                        <div style="margin: 8px 0; padding: 8px; border: 1px solid #ddd; border-radius: 4px;">
                            <strong>${item.videoName}</strong> (${item.downloadList.length} files)
                            <ul style="margin: 4px 0 0 20px; padding: 0;">${fileList}</ul>
                        </div>
                    `;
                })
                .join('');

            const dialogHtml = `
                <div style="text-align: left; max-height: 400px; overflow-y: auto;">
                    <p><strong>Folder:</strong> ${folderName}</p>
                    <p><strong>Videos:</strong> ${allDownloadLists.length}</p>
                    <p><strong>Total Files:</strong> ${totalFiles}</p>
                    <hr style="margin: 10px 0;">
                    <div>${videoListHtml}</div>
                </div>
            `;

            Swal.fire({
                title: 'Download Folder Contents',
                html: dialogHtml,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: `Download All (${totalFiles} files)`,
                cancelButtonText: 'Cancel',
                width: '700px',
                backdrop: true,
                allowOutsideClick: false,
                customClass: {
                    popup: 'folder-download-dialog download-confirmation-dialog'
                },
                didOpen: () => {
                    // Ensure dialog is on top
                    const popup = Swal.getPopup();
                    if (popup) {
                        popup.style.zIndex = '99999';
                    }
                }
            }).then((result) => {
                resolve(result.isConfirmed);
            });
        });
    }

    /**
     * Execute folder downloads - creates separate ZIP files or one combined ZIP
     */
    async executeFolderDownloads(allDownloadLists, folderName) {
        const mainZip = new JSZip();
        let totalSuccessCount = 0;
        let totalErrorCount = 0;
        let processedVideos = 0;

        // Show progress dialog
        Swal.fire({
            title: 'Downloading Folder Contents',
            html: `
                <div style="text-align: left;">
                    <p>Processing videos: <span id="video-progress">0/${allDownloadLists.length}</span></p>
                    <p>Files processed: <span id="file-progress">0</span></p>
                    <div style="width: 100%; background-color: #f0f0f0; border-radius: 4px; margin: 10px 0;">
                        <div id="progress-bar" style="width: 0%; height: 20px; background-color: #007bff; border-radius: 4px; transition: width 0.3s;"></div>
                    </div>
                </div>
            `,
            allowOutsideClick: false,
            showConfirmButton: false,
        });

        for (const videoItem of allDownloadLists) {
            try {
                processedVideos++;
                
                // Update progress
                document.getElementById('video-progress').textContent = `${processedVideos}/${allDownloadLists.length}`;
                const overallProgress = (processedVideos / allDownloadLists.length) * 100;
                document.getElementById('progress-bar').style.width = `${overallProgress}%`;

                // Add files directly to root of zip with video name prefix for uniqueness
                let videoSuccessCount = 0;
                let videoErrorCount = 0;

                for (const file of videoItem.downloadList) {
                    try {
                        const fileContent = await this.getFileContent(file);
                        
                        // Create unique filename by prefixing with video name
                        const uniqueFileName = `${videoItem.videoName}_${file.name}`;

                        if (file.type === 'text') {
                            mainZip.file(uniqueFileName, fileContent);
                        } else if (file.type === 'vtt-to-srt') {
                            mainZip.file(uniqueFileName, fileContent);
                        } else {
                            mainZip.file(uniqueFileName, fileContent);
                        }

                        videoSuccessCount++;
                        totalSuccessCount++;

                        // Update file progress
                        document.getElementById('file-progress').textContent = totalSuccessCount;

                    } catch (error) {
                        console.error(`Failed to process ${file.name}:`, error);
                        videoErrorCount++;
                        totalErrorCount++;
                    }
                }

            } catch (error) {
                console.error(`Failed to process video ${videoItem.videoName}:`, error);
                totalErrorCount++;
            }
        }

        try {
            // Generate final ZIP file
            Swal.update({
                html: `
                    <div style="text-align: left;">
                        <p>Creating final ZIP file...</p>
                        <p>Total files processed: ${totalSuccessCount}</p>
                        <div style="width: 100%; background-color: #f0f0f0; border-radius: 4px; margin: 10px 0;">
                            <div style="width: 100%; height: 20px; background-color: #28a745; border-radius: 4px;"></div>
                        </div>
                    </div>
                `
            });

            const zipContent = await mainZip.generateAsync({ 
                type: 'blob',
                compression: 'DEFLATE',
                compressionOptions: { level: 6 }
            });
            
            const currentTime = new Date().toISOString().replace(/[:.]/g, '-');
            const zipFileName = `${folderName}_${currentTime}.zip`;

            // Download the ZIP file
            this.downloadBlob(zipContent, zipFileName);

            Swal.close();

            // Show completion message
            Swal.fire({
                title: 'Folder Download Complete',
                html: `
                    <div style="text-align: left;">
                        <p><strong>Folder:</strong> ${folderName}</p>
                        <p><strong>Videos processed:</strong> ${processedVideos}</p>
                        <p><strong>Files downloaded:</strong> ${totalSuccessCount}</p>
                        <p><strong>Errors:</strong> ${totalErrorCount}</p>
                        <p><strong>ZIP file:</strong> ${zipFileName}</p>
                    </div>
                `,
                icon: totalSuccessCount > 0 ? 'success' : 'error',
            });

        } catch (error) {
            Swal.close();
            throw new Error(`Failed to create folder ZIP file: ${error.message}`);
        }
    }
}

const videoDownloadServiceInstance = new VideoDownloadService();
export default videoDownloadServiceInstance;
